import { NextSeo } from 'next-seo'

import useSWR from 'swr'

import { CategoriesDocument } from '@/generated/contentful'

import { client } from '@/lib/contentful/client'
import { getBaseUrl, serializeCacheKey } from '@/lib/contentful/utils'

import { getSeoLocale } from '@/utils/locales/getSeoLocale'
import { generateSeoUrl } from '@/utils/seo/generateSeoUrl'
import { useUrl } from '@/utils/url/useUrl'

type Props = {
	locale: string | undefined
	parentCategorySlug?: string | null
	childCategorySlug?: string | null
	children: React.ReactNode
}

export function AppCategoryMeta({
	locale = 'en-US',
	parentCategorySlug,
	childCategorySlug,
	children
}: Props) {
	const url = useUrl()

	const slug = childCategorySlug ?? parentCategorySlug

	const getAsPath = () => {
		if (parentCategorySlug && childCategorySlug) {
			return `/apps/categories/${parentCategorySlug}/${childCategorySlug}/`
		}
		if (parentCategorySlug) {
			return `/apps/categories/${parentCategorySlug}/`
		}
		return `/apps/categories/`
	}

	const asPath = getAsPath()

	const { data } = useSWR(slug && serializeCacheKey('appCategory', { slug, locale: 'en-US' }), () =>
		client().request(CategoriesDocument, { where: { slug }, limit: 1, locale: 'en-US' })
	)

	const entry = data?.categoryCollection?.items?.[0]

	if (!entry?.slug) {
		return (
			<>
				<NextSeo
					title={'All | BigCommerce'}
					description="Discover 100s of ecommerce apps and integrations in the BigCommerce Apps Marketplace ranging from advertising and analytics and email to marketing and mobile and multichannel listings."
					canonical={entry?.seoMetadata?.canonicalUrl || generateSeoUrl(url?.href)}
					languageAlternates={[
						{ hrefLang: 'en-au', href: `${getBaseUrl('en-AU')}${asPath}` },
						{ hrefLang: 'en-gb', href: `${getBaseUrl('en-GB')}${asPath}` },
						{ hrefLang: 'en-us', href: `${getBaseUrl('en-US')}${asPath}` },
						{ hrefLang: 'x-default', href: `${getBaseUrl()}${asPath}` }
					]}
					openGraph={{
						url: generateSeoUrl(url?.href)
					}}
				/>

				{children}
			</>
		)
	}

	const { seoMetadata } = entry ?? {}

	const title = seoMetadata?.title?.endsWith('| BigCommerce')
		? seoMetadata.title
		: `${seoMetadata?.title} | BigCommerce`

	const description =
		seoMetadata?.description ??
		'Discover 100s of ecommerce apps and integrations in the BigCommerce Apps Marketplace ranging from advertising and analytics and email to marketing and mobile and multichannel listings.'

	return (
		<>
			<NextSeo
				title={title}
				description={description}
				canonical={entry?.seoMetadata?.canonicalUrl || `${url?.protocol}//${url?.host}${asPath}`}
				languageAlternates={[
					{ hrefLang: 'en-au', href: `${getBaseUrl('en-AU')}${asPath}` },
					{ hrefLang: 'en-gb', href: `${getBaseUrl('en-GB')}${asPath}` },
					{ hrefLang: 'en-us', href: `${getBaseUrl('en-US')}${asPath}` },
					{ hrefLang: 'x-default', href: `${getBaseUrl()}${asPath}` }
				]}
				openGraph={{
					title: title,
					description: description,
					url: `${url?.protocol}//${url?.host}${asPath}`,
					siteName: 'BigCommerce',
					images: seoMetadata?.image?.url
						? [
								{
									url: seoMetadata?.image?.url,
									width: seoMetadata?.image?.width,
									height: seoMetadata?.image?.height,
									alt: 'Bigcommerce logo',
									type: 'image/jpeg'
								}
						  ]
						: undefined,
					locale: getSeoLocale(locale)
				}}
				nofollow={Boolean(seoMetadata?.nofollow)}
				noindex={Boolean(seoMetadata?.noindex)}
			/>

			{children}
		</>
	)
}
